import { CommonModule, NgForOf } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { FileService } from '../../file/file.service';
import { BaseComponent } from '../../shared/base.component';
import { ViewLogsState } from '../../shared/state-interface';
import { UserType } from '../../shared/user-type.model';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { CustomerNamesComponent } from '../../ui-components/customer-names/customer-names.component';
import { DealerNamesComponent } from '../../ui-components/dealer-names/dealer-names.component';
import { EmployeeNamesComponent } from '../../ui-components/employee-names/employee-names.component';
import { UploadedByTypesComponent } from "../../ui-components/uploaded-by-types/uploaded-by-types.component";
import { LogFile } from '../shared/log-file.model';
import { CustomPaginatorIntl } from './custom-paginator';

@Component({
    selector: 'app-view-logs',
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    NgForOf,
    SiteHeaderComponent,
    EmployeeNamesComponent,
    DealerNamesComponent,
    CustomerNamesComponent,
    UploadedByTypesComponent
],
    providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }],
    templateUrl: './view-logs.component.html',
    styleUrl: './view-logs.component.css'
})
export class ViewLogsComponent extends BaseComponent implements AfterViewInit {

    @ViewChild(UploadedByTypesComponent) uploadedByTypesComponent!:UploadedByTypesComponent;
    @ViewChild(EmployeeNamesComponent) employeeNamesComponent!:EmployeeNamesComponent;
    @ViewChild(DealerNamesComponent) dealerNamesComponent!:DealerNamesComponent;
    @ViewChild(CustomerNamesComponent) customerNamesComponent!:CustomerNamesComponent;

    private fileService: FileService = inject(FileService);

    private static _retrievingLogFiles = "Retrieving Log Files";
    private static _retrievingLogFilesError = `${ViewLogsComponent._retrievingLogFiles} error:`;
    private static _downloadingLogFile = "Downloading Log File";
    private static _downloadingLogFileError = `${ViewLogsComponent._downloadingLogFile} error:`;
    private static _deletingLogFile = "Deleting Log File";
    private static _deletingLogFileError = `${ViewLogsComponent._deletingLogFile} error:`;

    protected uploadedByTypes: UserType[] = []

    private _logFiles: WritableSignal<LogFile[]> = signal<LogFile[]>([]);
    private _logFileCount: WritableSignal<number> = signal<number>(-1);

    private _endingLogFiles: string[] = [];

    private _lastAction: number = 0;

    public ngAfterViewInit() {
        // Assumption (reality as of writing these comments):
        //
        //    - The uploadedByTypesComponent will be undefined if
        //     <app-uploaded-by-types-component/> is not rendered
        //     in the HTML as a result of a surrounding *if* condition.
        //
        //   - If uploadedByTypesComponent is undefined, then it
        //     means the HTML page omits search options **and** omits
        //     search button.
        //
        //   - If uploadedByTypesComponent is not undefined, then it
        //     means the HTML page includes search options **and**
        //     includes search button.
        //
        //   - If search button is absent from the page, then bypass saving
        //     and restoring page state. Automatically initiate reload upon
        //     each return to this page. This design is relevant in a Use
        //     Case where the user switches back and forth between uploading
        //     log files and viewing the list of uploaded logs.
        //
        //   - If search button is present on the page, then the user
        //     can refresh page at will. In this Use Case, save and restore
        //     page state and only load log files upon the user clicking the
        //     search button.
        const state: ViewLogsState | undefined = this.stateManager.viewLogsState;

        if (state != undefined) {
            if (this.refresh) {
                this.pageIndex = 0;
                this.logFileCount = -1;
                this.listLogFiles(this.overrideSnackBar);
            } else {
                this.logFiles = state.logFiles;
                this.logFileCount = state.logFileCount;
                this._endingLogFiles = state.endingLogfiles;
                this.employeeNamesComponent.restoreState(state.employeeNamesComponentState!);
                this.dealerNamesComponent.restoreState(state.dealerNamesComponentState!);
                this.customerNamesComponent.restoreState(state.customerNamesComponentState!);
                this.uploadedByTypesComponent.restoreState(state.uploadedByTypeComponentState!);
                super.restoreState(state.baseComponentState);
            }

        } else if (this.uploadedByTypesComponent != undefined) {
            this.employeeNamesComponent.show = false;
            this.dealerNamesComponent.show = false;
            this.customerNamesComponent.show = false;

            this.uploadedByTypesComponent.init();
            this.employeeNamesComponent.init();
            this.dealerNamesComponent.init();
            this.customerNamesComponent.init();

        } else {
            this.listLogFiles();
        }
    }

    public ngOnDestroy() {
        if (this.uploadedByTypesComponent != undefined) {
            const state: ViewLogsState = {} as ViewLogsState;
            state.logFiles = this.logFiles;
            state.logFileCount = this.logFileCount;
            state.endingLogfiles = this._endingLogFiles;
            state.uploadedByTypeComponentState = this.uploadedByTypesComponent.getState();
            state.employeeNamesComponentState = this.employeeNamesComponent.getState();
            state.dealerNamesComponentState = this.dealerNamesComponent.getState();
            state.customerNamesComponentState = this.customerNamesComponent.getState();
            state.baseComponentState = super.getState();
            this.stateManager.viewLogsState = state;
        }
    }

    public receiveUploadedByType(uploadedByType: string) {
        if (uploadedByType == "employee") {
            this.dealerNamesComponent.show = false;
            this.customerNamesComponent.show = false;
            this.employeeNamesComponent.show = true;

        } else if (uploadedByType == "dealer") {
            this.employeeNamesComponent.show = false;
            this.customerNamesComponent.show = false;
            this.dealerNamesComponent.show = true;

        } else if (uploadedByType == "customer") {
            this.employeeNamesComponent.show = false;
            this.dealerNamesComponent.show = false;
            this.customerNamesComponent.show = true;
        } else {
            this.employeeNamesComponent.show = false;
            this.dealerNamesComponent.show = false;
            this.customerNamesComponent.show = false;
        }
    }

    protected get logFiles(): LogFile[] {
        return this._logFiles();
    }

    private set logFiles(value: LogFile[]) {
        this._logFiles.set(value);
    }

    protected get logFileCount(): number {
        return this._logFileCount();
    }

    protected override handlePageEvent(e: PageEvent): void {
        this._lastAction = this.pageIndex > e.pageIndex ? -1 : 1;
        const pageSize = this.pageSize;
        super.handlePageEvent(e);

        // If pageSize has changed, then reset pageIndex to page,
        // 1. This differs from other pages where the data comes
        // from the database and where 'offset' is supported in the
        // database query, and thus we can use the database offset
        // capability to refresh the current page with the new page
        // size.
        if (pageSize != this.pageSize) {
            this.pageIndex = 0;
        }

        this.listLogFiles();
    }

    protected async listLogFiles(): Promise<void>;
    protected async listLogFiles(overrideSnackBar: string | undefined): Promise<void>;
    protected async listLogFiles(overrideSnackBar?: string): Promise<void> {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            if (this.searchParamsDirty()) {
                this.pageIndex = 0;
                this.logFileCount = -1;
                this.markSearchParams(super.pristine);
            }

            let endingLogFileName: string = "";

            if (this.pageIndex > 0) {
                if (this.isLastActionPageForward) {
                    endingLogFileName = this.logFiles[this.logFiles.length - 1].name;
                } else if (this.isLastActionPageBackward) {
                    endingLogFileName = this._endingLogFiles[this._endingLogFiles.length - 2];
                } else {
                    // Last action == Delete.
                    endingLogFileName = this._endingLogFiles[this._endingLogFiles.length - 1];
                }

            } else {
                this._endingLogFiles = [];
            }

            const observable = this.fileService.getFiles(await getIdToken(currentUser), "logs",
                    this.getSearchParams(), this.pageSize, this.pageIndex, endingLogFileName)

            if (overrideSnackBar == undefined) {
                super.showSnackBar(`${ViewLogsComponent._retrievingLogFiles}...`, -1)
            }

            super.message = "";

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-logs"] == "success") {
                        if (this.isLastActionPageForward) {
                            if (this.pageIndex > 0) {
                                this._endingLogFiles.push(endingLogFileName);
                            }
                        } else if (this.isLastActionPageBackward) {
                            this._endingLogFiles.pop();
                        } else {
                            // Last Action == Delete.
                            // Do nothing with endingLogFiles.
                        }

                        // this.logFiles is a signal. The view will be refreshed
                        // upon update of the signal.
                        this.logFiles = LogFile.toLogFiles(json["files"]);
                        this.logFileCount = (this.pageIndex * this.pageSize) + json["file-count"];

                        // If there are more log files in the backend storage bucket, then increase
                        // logFilecount by an additional pageSize + 1. We need to increase the
                        // logFileCount to enable the paginator Next Page button.
                        // 
                        // Technically, we only need to increase the logFileCount by 1 for purposes
                        // of enabling the Next Page button. However, the reason we increase logFileCount
                        // by pageSize + 1 is to support logic in the custom-paginator.ts file.
                        //
                        // If we only increased the logFileCount by 1, then in the customer-paginator.ts
                        // we would not be able to differentiate between additional log files available
                        // in the backend storage bucket versus the last page of log files having only
                        // one log file.
                        //
                        // The logFileCount here is the 'length' parameter in the custom-paginator.ts file.
                        if (json["is-more"]) {
                            this.logFileCount = this.logFileCount + this.pageSize + 1;
                        }
                        overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar);
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    const message = `${ViewLogsComponent._retrievingLogFilesError} ${(<Error>error).message}`;
                    console.error(message);
                    super.message = message;
                }
            });
        } else {
            const message = `${ViewLogsComponent._retrievingLogFilesError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async resetSearch() {
        if (this.uploadedByTypesComponent != undefined) {
            this.uploadedByTypesComponent.uploadedByTypeCtl.setValue("");
            this.employeeNamesComponent.employeeCtl.setValue("");
            this.dealerNamesComponent.dealerCtl.setValue("");
            this.customerNamesComponent.customerCtl.setValue("");
            this.markSearchParams(super.dirty);
            this.listLogFiles();
        }
    }

    protected async downloadFile(fileNameLong: string, fileNameShort: string) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = this.fileService.getDownloadUrl(await getIdToken(currentUser), "log", fileNameLong);
            super.showSnackBar(`${ViewLogsComponent._downloadingLogFile}...`, -1);

            try {
                observable.subscribe(response => {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-log-download-url"] == "success") {
                        this.initiateDownload(json.url, fileNameShort);
                    } else {
                        throw new Error(json["message"]);
                    }
                });
            } catch (error) {
                const message = `${ViewLogsComponent._downloadingLogFileError} ${(<Error>error).message}`;
                console.error(message);
                super.showSnackBar(message, 30);
           }
        } else {
            const message = `${ViewLogsComponent._downloadingLogFileError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    private async initiateDownload(url: string, fileName: string) {
        const observable = this.fileService.downloadFile(url);
        const downloadSub = observable.subscribe(event => {
            try {
                switch (event.type) {
                    case HttpEventType.Sent:
                        break;
                    case HttpEventType.ResponseHeader:
                        break;
                    case HttpEventType.DownloadProgress:
                        /*
                        let percentDone = 0;
                        if (event.total != undefined) {
                            percentDone = Math.round(100 * event.loaded / event.total);
                        }
                        console.log(`Download progress: ${percentDone}%`);
                        */
                        break;
                    case HttpEventType.Response:
                        if (event.status == 200) {
                            const blob = new Blob([event.body as BlobPart], { type: 'application/octet-stream' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = fileName; 
                            link.click();
                            link.remove();
                            this.showSnackBar(`Download completed.`);
                        } else {
                            throw new Error(`Download failed, status: ${event.status}, statusText: ${event.statusText}`)
                        }
                        break;
                }
            } catch (error) {
                const message = `${ViewLogsComponent._downloadingLogFileError} ${(<Error>error).message}`;
                console.error(message);
                super.showSnackBar(message, 30);
            }
        });
    }

    protected async deleteFile(fileName: string) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = this.fileService.deleteFile(await getIdToken(currentUser), "log", fileName);
            super.showSnackBar(`${ViewLogsComponent._deletingLogFile}...`, -1);
            
            try {
                observable.subscribe(response => {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["delete-log"] == "success") {
                        if (this.logFiles.length == 1) {
                            // Nothing left on current page, backup
                            // one page.
                            this.pageIndex -= 1;
                            this._endingLogFiles.pop();
                        }

                        this.setLastActionDelete();
                        this.listLogFiles(`Log file deleted.`);
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                });
            } catch (error) {
                const message = `${ViewLogsComponent._deletingLogFileError} ${(<Error>error).message}`;
                console.error(message);
                super.showSnackBar(message, 30);
            }
        } else {
            const message = `${ViewLogsComponent._deletingLogFileError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected searchParamsDirty(): boolean {
        if (this.uploadedByTypesComponent != undefined) {
            return this.uploadedByTypesComponent.uploadedByTypeCtl.dirty
                || this.employeeNamesComponent.employeeCtl.dirty
                || this.dealerNamesComponent.dealerCtl.dirty
                || this.customerNamesComponent.customerCtl.dirty
        } else {
            return false;
        }
    }

    protected markSearchParams(func: Function) {
        super.markControl(this.uploadedByTypesComponent.uploadedByTypeCtl, func);
        super.markControl(this.employeeNamesComponent.employeeCtl, func);
        super.markControl(this.dealerNamesComponent.dealerCtl, func);
        super.markControl(this.customerNamesComponent.customerCtl, func);
    }

    private getSearchParams(): any {
        let searchParams = {};

        if (this.uploadedByTypesComponent != undefined) {
            const uploadedByType = this.uploadedByTypesComponent.selectedUploadByType;
            let uploadedById: number | undefined;

            if (uploadedByType == "employee") {
                uploadedById = (this.employeeNamesComponent.selectedEmployee > 0 ?
                                    this.employeeNamesComponent.selectedEmployee : undefined);
            } else if (uploadedByType == "dealer") {
                uploadedById = (this.dealerNamesComponent.selectedDealer > 0 ?
                                    this.dealerNamesComponent.selectedDealer : undefined);
            } if (uploadedByType == "customer") {
                uploadedById = (this.customerNamesComponent.selectedCustomer > 0 ?
                                    this.customerNamesComponent.selectedCustomer : undefined);
            }

            searchParams = {"uploaded-by-type": uploadedByType, "uploaded-by-id": uploadedById};
        }

        return searchParams;
    }

    private set logFileCount(value: number) {
        this._logFileCount.set(value);
    }

    private get isLastActionPageBackward(): boolean {
        return this._lastAction == -1;
    }

    private get isLastActionPageForward(): boolean {
        return this._lastAction == 1;
    }

    private setLastActionDelete(): void {
        this._lastAction = 0;
    }
}
